import React from 'react';
import { makeStyles, Theme } from '@material-ui/core';
import { createStyles } from '@material-ui/styles';
import clsx from 'clsx';
import BaseTypography from 'src/components/Text/BaseTypography';
import { CreateButton, drawerWidth } from 'src/components/UI';
import { CreateChannelButtonProps } from 'src/components/Layout/ChannelsPageLayout/channelsPageLayoutTypes';
import { Searchbox } from 'src/components/SearchInput/Searchbox';
import { MENU_LOGO_WIDTH } from 'src/components/Navbar/MenuLogo';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      [theme.breakpoints.up('sm')]: {
        width: drawerWidth - MENU_LOGO_WIDTH,
      },
      [theme.breakpoints.up('md')]: {
        width: drawerWidth,
      },
      [theme.breakpoints.down('xs')]: {
        // width:0 and minWidth:100% prevents div from expanding
        // when search input width changes
        width: '0px',
        minWidth: `calc(100% - ${MENU_LOGO_WIDTH}px)`,
      },
    },
    titleWrapper: {
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    actionsWrapper: {
      display: 'flex',
      height: '100%',
      alignItems: 'center',
      gap: theme.spacing(1.5),
    },
    flexEnd: {
      justifyContent: 'flex-end',
    },
    hiddenListToolbar: {
      [theme.breakpoints.down('xs')]: {
        // on mobile screen, hide the left toolbar (channel list)
        // when the secondary sidebar is not mount.
        display: 'none',
      },
    },
  }),
);

type ChannelsListToolbarProps = {
  pageTitle: string;
  createButtonProps: CreateChannelButtonProps;
  isSecondarySidebarOpen: boolean;
};
export const ChannelsListToolbar: React.FC<ChannelsListToolbarProps> = ({
  pageTitle,
  createButtonProps,
  isSecondarySidebarOpen,
}) => {
  const classes = useStyles({ isSecondarySidebarOpen });
  const actionWrapperRef = React.useRef<HTMLDivElement>(null);
  const rootRef = React.useRef<HTMLDivElement>(null);
  // width of search input when expanded
  const [fullSearchWidth, setFullSearchWidth] = React.useState(0);

  const getSearchExpandWidth = () => {
    const rootWidth = rootRef.current?.clientWidth || 0;
    const actionWrapperWidth = actionWrapperRef.current?.clientWidth || 0;

    // space between search icon and left toolbar start
    setFullSearchWidth(rootWidth - actionWrapperWidth);
  };

  React.useLayoutEffect(() => {
    getSearchExpandWidth();
    window.addEventListener('resize', getSearchExpandWidth);
    return () => window.removeEventListener('resize', getSearchExpandWidth);
  }, [rootRef.current, actionWrapperRef.current]);

  return (
    <div
      className={clsx(classes.root, {
        [classes.hiddenListToolbar]: !isSecondarySidebarOpen,
      })}
      ref={rootRef}
    >
      <BaseTypography className={classes.titleWrapper} fontType="13Medium">
        {pageTitle}
      </BaseTypography>

      <div className={classes.actionsWrapper} ref={actionWrapperRef}>
        <Searchbox
          fullWidthOnFocus
          showIconOnly
          expandWidth={fullSearchWidth}
        />
        {createButtonProps && (
          <CreateButton
            iconOnly
            // the create button is wrapped by a button group. We need to pass the correct color and variant also to
            // the button group to keep the button group style consistent with the create button.
            buttonGroupProps={{
              color: 'secondary',
              variant: 'contained',
            }}
            color="secondary"
            variant="contained"
            {...createButtonProps}
          />
        )}
      </div>
    </div>
  );
};
